import EvaluationFlow from "../../../../flow/EvaluationFlow"
import { EvaluatorContext } from "../../../../evaluator/Evaluator"
import { DecisionReason } from "../../../../../model/model"
import { InAppMessageEligibilityEvaluateResult } from "../InAppMessageEligibilityEvaluateResult"
import { InAppMessageEligibilityLocalEvaluateRequest } from "../mode/local/InAppMessageEligibilityLocalEvaluateRequest"
import InAppMessageEligibilityFlowEvaluator from "./InAppMessageEligibilityFlowEvaluator"
import { InAppMessageMatcher } from "../match/InAppMessageMatcher"
import { InAppMessageLayoutLocalEvaluateRequest } from "../../layout/mode/local/InAppMessageLayoutLocalEvaluateRequest"
import { InAppMessageLayoutLocalEvaluator } from "../../layout/mode/local/InAppMessageLayoutLocalEvaluator"
import { InAppMessageLayoutEvaluateResponse } from "../../layout/InAppMessageLayoutEvaluateResponse"

export type InAppMessageEligibilityLocalFlow = EvaluationFlow<
  InAppMessageEligibilityLocalEvaluateRequest,
  InAppMessageEligibilityEvaluateResult
>

export interface InAppMessageEligibilityLocalFlowEvaluator
  extends InAppMessageEligibilityFlowEvaluator<InAppMessageEligibilityLocalEvaluateRequest> {
  evaluate(
    request: InAppMessageEligibilityLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityLocalFlow
  ): InAppMessageEligibilityEvaluateResult | undefined
}

export class OverrideInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityLocalFlowEvaluator {
  constructor(private readonly userOverrideMatcher: InAppMessageMatcher) {}

  evaluate(
    request: InAppMessageEligibilityLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityLocalFlow
  ): InAppMessageEligibilityEvaluateResult | undefined {
    const isOverrideMatched = this.userOverrideMatcher.matches(request, context)
    if (isOverrideMatched) {
      return InAppMessageEligibilityEvaluateResult.eligible(DecisionReason.OVERRIDDEN)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class DraftInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityLocalFlowEvaluator {
  evaluate(
    request: InAppMessageEligibilityLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityLocalFlow
  ): InAppMessageEligibilityEvaluateResult | undefined {
    const isDraft = request.inAppMessage.status === "DRAFT"
    if (isDraft) {
      return InAppMessageEligibilityEvaluateResult.ineligible(DecisionReason.IN_APP_MESSAGE_DRAFT)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class PauseInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityLocalFlowEvaluator {
  evaluate(
    request: InAppMessageEligibilityLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityLocalFlow
  ): InAppMessageEligibilityEvaluateResult | undefined {
    const isPause = request.inAppMessage.status === "PAUSE"
    if (isPause) {
      return InAppMessageEligibilityEvaluateResult.ineligible(DecisionReason.IN_APP_MESSAGE_PAUSED)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class TargetInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityLocalFlowEvaluator {
  constructor(private readonly targetMatcher: InAppMessageMatcher) {}

  evaluate(
    request: InAppMessageEligibilityLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityLocalFlow
  ): InAppMessageEligibilityEvaluateResult | undefined {
    const isTargetMatched = this.targetMatcher.matches(request, context)
    if (!isTargetMatched) {
      return InAppMessageEligibilityEvaluateResult.ineligible(DecisionReason.NOT_IN_IN_APP_MESSAGE_TARGET)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class LayoutResolveInAppMessageEligibilityFlowEvaluator implements InAppMessageEligibilityLocalFlowEvaluator {
  constructor(private readonly layoutEvaluator: InAppMessageLayoutLocalEvaluator) {}

  evaluate(
    request: InAppMessageEligibilityLocalEvaluateRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageEligibilityLocalFlow
  ): InAppMessageEligibilityEvaluateResult | undefined {
    const layoutRequest = InAppMessageLayoutLocalEvaluateRequest.of(request)
    const layoutResponse = this.layoutEvaluator.evaluate(layoutRequest, EvaluatorContext.create())
    context.set(InAppMessageLayoutEvaluateResponse, layoutResponse)

    return nextFlow.evaluate(request, context)
  }
}
